import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { 
  Calendar, ChevronLeft, ChevronRight, Users, AlertTriangle, 
  Clock, CheckCircle2, XCircle, Info, Loader2, Filter, Table, CalendarDays,
  Palmtree, Stethoscope, Home, Baby, PartyPopper, Video
} from 'lucide-react';
import adminApi from '../lib/adminApi';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import LeaveManagementTable from './LeaveManagementTable';
import { formatDateShort } from '../lib/dateUtils';
import { HeaderSkeleton, MetricCardsSkeleton, TableSkeleton } from './skeletons/Skeletons';

// Department color palette - distinct colors for each department
const DEPARTMENT_COLORS = {
  // Predefined common departments
  'Boss': { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800', dot: 'bg-purple-500' },
  'Content': { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800', dot: 'bg-blue-500' },
  'Design': { bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-800', dot: 'bg-pink-500' },
  'Development': { bg: 'bg-emerald-100', border: 'border-emerald-300', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  'Marketing': { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', dot: 'bg-orange-500' },
  'Sales': { bg: 'bg-cyan-100', border: 'border-cyan-300', text: 'text-cyan-800', dot: 'bg-cyan-500' },
  'HR': { bg: 'bg-violet-100', border: 'border-violet-300', text: 'text-violet-800', dot: 'bg-violet-500' },
  'Finance': { bg: 'bg-amber-100', border: 'border-amber-300', text: 'text-amber-800', dot: 'bg-amber-500' },
  'Operations': { bg: 'bg-teal-100', border: 'border-teal-300', text: 'text-teal-800', dot: 'bg-teal-500' },
  'Production': { bg: 'bg-rose-100', border: 'border-rose-300', text: 'text-rose-800', dot: 'bg-rose-500' },
};

// Dynamic color generator for departments not in predefined list
const COLOR_PALETTE = [
  { bg: 'bg-indigo-100', border: 'border-indigo-300', text: 'text-indigo-800', dot: 'bg-indigo-500' },
  { bg: 'bg-lime-100', border: 'border-lime-300', text: 'text-lime-800', dot: 'bg-lime-500' },
  { bg: 'bg-fuchsia-100', border: 'border-fuchsia-300', text: 'text-fuchsia-800', dot: 'bg-fuchsia-500' },
  { bg: 'bg-sky-100', border: 'border-sky-300', text: 'text-sky-800', dot: 'bg-sky-500' },
  { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', dot: 'bg-red-500' },
  { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800', dot: 'bg-yellow-500' },
];

// Leave type icons (Lucide components — replaces previous emoji set)
const LEAVE_ICONS = {
  pl: Palmtree,        // Privilege / personal
  sl: Stethoscope,     // Sick
  wfh: Home,           // Work from home
  ml: Baby,            // Maternity / parental
  ol: PartyPopper,     // Optional / occasion
  prod: Video,         // Production
};

export default function LeaveCalendar() {
  const [leaves, setLeaves] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('approved');
  const [hoveredLeave, setHoveredLeave] = useState(null);
  const [activeView, setActiveView] = useState('table'); // 'calendar' or 'table' - default to table

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [leavesRes, deptsRes, empsRes] = await Promise.all([
        adminApi.get('/api/admin/leaves'),
        adminApi.get('/api/admin/departments'),
        adminApi.get('/api/admin/employees')
      ]);
      setLeaves(leavesRes.data || []);
      setDepartments(deptsRes.data || []);
      setEmployees(empsRes.data || []);
    } catch (error) {
      toast.error('Failed to load calendar data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Generate department color map
  const departmentColorMap = useMemo(() => {
    const map = {};
    let colorIndex = 0;
    
    departments.forEach(dept => {
      const deptName = dept.name;
      if (DEPARTMENT_COLORS[deptName]) {
        map[dept._id] = { ...DEPARTMENT_COLORS[deptName], name: deptName };
      } else {
        map[dept._id] = { ...COLOR_PALETTE[colorIndex % COLOR_PALETTE.length], name: deptName };
        colorIndex++;
      }
    });
    
    return map;
  }, [departments]);

  // Get employee name - check multiple sources
  const getEmployeeName = useCallback((leave) => {
    // First check if leave has employee object directly
    if (leave.employee?.name) return leave.employee.name;
    // Then check employee_name field
    if (leave.employee_name) return leave.employee_name;
    // Finally try to find in employees list
    const employee = employees.find(e => e._id === leave.employee_id);
    return employee?.name || 'Unknown';
  }, [employees]);

  // Get employee's primary department from leave or employees list
  const getEmployeeDepartment = useCallback((leave) => {
    // First check if employee object has departments
    if (leave.employee?.departments?.length > 0) {
      return leave.employee.departments[0];
    }
    // Then check in employees list
    const employee = employees.find(e => e._id === leave.employee_id);
    if (!employee) return null;
    
    const deptIds = employee.departments || employee.employment?.departments || [];
    if (deptIds.length > 0) {
      return deptIds[0]; // Return first department as primary
    }
    return null;
  }, [employees]);

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get calendar data
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // First day of month
    const firstDay = new Date(year, month, 1);
    const startingDay = firstDay.getDay(); // 0 = Sunday
    
    // Last day of month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Create calendar grid (6 weeks x 7 days)
    const weeks = [];
    let currentDay = 1 - startingDay;
    
    for (let week = 0; week < 6; week++) {
      const days = [];
      for (let day = 0; day < 7; day++) {
        if (currentDay > 0 && currentDay <= totalDays) {
          days.push({
            date: new Date(year, month, currentDay),
            day: currentDay,
            isCurrentMonth: true,
            isToday: new Date().toDateString() === new Date(year, month, currentDay).toDateString()
          });
        } else {
          // Days from previous or next month
          const otherDate = new Date(year, month, currentDay);
          days.push({
            date: otherDate,
            day: otherDate.getDate(),
            isCurrentMonth: false,
            isToday: false
          });
        }
        currentDay++;
      }
      weeks.push(days);
      
      // Stop if we've passed the last day
      if (currentDay > totalDays && week >= 3) break;
    }
    
    return weeks;
  }, [currentDate]);

  // Get leaves for a specific date
  const getLeavesForDate = useCallback((date) => {
    // Format date as YYYY-MM-DD using local timezone (not UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return leaves.filter(leave => {
      // Status filter
      if (selectedStatus !== 'all' && leave.status !== selectedStatus) return false;
      
      // Date range check
      const startDate = leave.start_date;
      const endDate = leave.end_date;
      if (dateStr < startDate || dateStr > endDate) return false;
      
      // Department filter
      if (selectedDepartment !== 'all') {
        const empDeptId = getEmployeeDepartment(leave);
        if (empDeptId !== selectedDepartment) return false;
      }
      
      return true;
    });
  }, [leaves, selectedStatus, selectedDepartment, getEmployeeDepartment]);

  // Check for clashes (multiple people on leave from same department)
  const getClashInfo = useCallback((date) => {
    const dateLeaves = getLeavesForDate(date);
    const deptCounts = {};
    
    dateLeaves.forEach(leave => {
      const deptId = getEmployeeDepartment(leave);
      if (deptId) {
        deptCounts[deptId] = (deptCounts[deptId] || 0) + 1;
      }
    });
    
    // Find departments with more than 1 person on leave
    const clashes = Object.entries(deptCounts)
      .filter(([_, count]) => count > 1)
      .map(([deptId, count]) => ({
        departmentId: deptId,
        departmentName: departmentColorMap[deptId]?.name || 'Unknown',
        count
      }));
    
    return clashes;
  }, [getLeavesForDate, getEmployeeDepartment, departmentColorMap]);

  // Month name
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="leave-calendar-loading">
        <HeaderSkeleton />
        <MetricCardsSkeleton count={4} />
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="p-6" data-testid="leave-calendar">
        {/* Header with View Toggle */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-charcoal-900">Leave Management</h2>
            <p className="text-neutral-600 mt-1">View and manage team leaves across departments</p>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-neutral-100 p-1 rounded-xl">
            <button
              onClick={() => setActiveView('table')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeView === 'table' 
                  ? 'bg-white text-pink-600 shadow-soft' 
                  : 'text-neutral-600 hover:text-[#1a1a2e]'
              }`}
              data-testid="view-table-btn"
            >
              <Table className="w-4 h-4" />
              All Leaves
            </button>
            <button
              onClick={() => setActiveView('calendar')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeView === 'calendar' 
                  ? 'bg-white text-pink-600 shadow-soft' 
                  : 'text-neutral-600 hover:text-[#1a1a2e]'
              }`}
              data-testid="view-calendar-btn"
            >
              <CalendarDays className="w-4 h-4" />
              Calendar View
            </button>
          </div>
        </div>

        {/* Table View */}
        {activeView === 'table' && (
          <LeaveManagementTable />
        )}

        {/* Calendar View */}
        {activeView === 'calendar' && (
          <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-48 bg-white">
                <Filter className="w-4 h-4 mr-2 text-neutral-500" />
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept._id} value={dept._id}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${departmentColorMap[dept._id]?.dot || 'bg-gray-400'}`} />
                      {dept.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-40 bg-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

        {/* Department Legend */}
        <div className="glass-card p-4 mb-6">
          <h3 className="text-sm font-semibold text-charcoal-700 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Department Color Legend
          </h3>
          <div className="flex flex-wrap gap-3">
            {departments.map(dept => (
              <div 
                key={dept._id} 
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${departmentColorMap[dept._id]?.bg || 'bg-neutral-100'} ${departmentColorMap[dept._id]?.border || 'border-white/40'} border`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${departmentColorMap[dept._id]?.dot || 'bg-gray-400'}`} />
                <span className={`text-sm font-medium ${departmentColorMap[dept._id]?.text || 'text-neutral-700'}`}>
                  {dept.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar Navigation */}
        <div className="glass-card shadow-soft overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-charcoal-100 bg-charcoal-50">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={goToPreviousMonth} className="bg-white">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToNextMonth} className="bg-white">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday} className="bg-white">
                Today
              </Button>
            </div>
            <h3 className="text-xl font-bold text-charcoal-900">{monthName}</h3>
            <div className="flex items-center gap-2 text-sm">
              <span className="flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-charcoal-600">= Clash (multiple leaves)</span>
              </span>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  {dayNames.map(day => (
                    <th 
                      key={day} 
                      className={`px-1 md:px-2 py-2 md:py-3 text-center text-xs md:text-sm font-semibold border-b border-charcoal-100 ${
                        day === 'Sun' || day === 'Sat' ? 'text-neutral-500 bg-charcoal-25' : 'text-charcoal-700'
                      }`}
                    >
                      <span className="hidden md:inline">{day}</span>
                      <span className="md:hidden">{day.charAt(0)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calendarData.map((week, weekIdx) => (
                  <tr key={weekIdx}>
                    {week.map((dayData, dayIdx) => {
                      const dateLeaves = getLeavesForDate(dayData.date);
                      const clashes = getClashInfo(dayData.date);
                      const hasClash = clashes.length > 0;
                      const isWeekend = dayIdx === 0 || dayIdx === 6;
                      
                      return (
                        <td 
                          key={dayIdx}
                          className={`border border-charcoal-100 align-top transition-colors ${
                            !dayData.isCurrentMonth ? 'bg-charcoal-25' : 
                            dayData.isToday ? 'bg-pink-50' :
                            isWeekend ? 'bg-charcoal-25/50' : 'bg-white'
                          } hover:bg-charcoal-50/50`}
                          style={{ height: '80px', minHeight: '80px' }}
                        >
                          <div className="p-1 h-full flex flex-col">
                            {/* Date header */}
                            <div className="flex items-center justify-between mb-0.5">
                              <span className={`text-xs md:text-sm font-semibold ${
                                !dayData.isCurrentMonth ? 'text-neutral-400' :
                                dayData.isToday ? 'text-pink-600' : 'text-charcoal-700'
                              }`}>
                                {dayData.day}
                              </span>
                              {hasClash && dayData.isCurrentMonth && (
                                <Tooltip>
                                  <TooltipTrigger>
                                    <AlertTriangle className="w-3 h-3 md:w-4 md:h-4 text-amber-500" />
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-amber-50 border-amber-200 text-amber-800">
                                    <p className="font-semibold">Leave Clash!</p>
                                    {clashes.map(c => (
                                      <p key={c.departmentId}>{c.departmentName}: {c.count} people</p>
                                    ))}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            
                            {/* Leave entries */}
                            <div className="flex-1 overflow-y-auto space-y-0.5">
                              {dateLeaves.slice(0, 3).map((leave, idx) => {
                                const deptId = getEmployeeDepartment(leave);
                                const colors = departmentColorMap[deptId] || { bg: 'bg-neutral-100', text: 'text-neutral-700', border: 'border-white/40' };
                                const empName = getEmployeeName(leave);
                                
                                return (
                                  <Tooltip key={leave._id}>
                                    <TooltipTrigger asChild>
                                      <div 
                                        className={`px-1 py-0.5 rounded text-[10px] md:text-xs truncate cursor-pointer border ${colors.bg} ${colors.text} ${colors.border} hover:opacity-80 transition-opacity`}
                                        onMouseEnter={() => setHoveredLeave(leave)}
                                        onMouseLeave={() => setHoveredLeave(null)}
                                      >
                                        {(() => {
                                          const Icon = LEAVE_ICONS[leave.leave_type] || Calendar;
                                          return <Icon className="inline w-3 h-3 mr-0.5" />;
                                        })()}
                                        <span className="hidden md:inline">{empName.split(' ')[0]}</span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-white border shadow-lg p-3 max-w-xs">
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          {(() => {
                                            const Icon = LEAVE_ICONS[leave.leave_type] || Calendar;
                                            return <Icon className="w-5 h-5 text-charcoal-700" />;
                                          })()}
                                          <span className="font-semibold text-charcoal-900">{empName}</span>
                                        </div>
                                        <div className="text-sm space-y-1 text-charcoal-600">
                                          <p><span className="font-medium">Type:</span> {leave.leave_type_name || leave.leave_type?.toUpperCase()}</p>
                                          <p><span className="font-medium">Duration:</span> {leave.days_count} day(s)</p>
                                          <p><span className="font-medium">Dates:</span> {formatDateShort(leave.start_date)} - {formatDateShort(leave.end_date)}</p>
                                          <p><span className="font-medium">Status:</span> 
                                            <Badge className={`ml-1 text-xs ${
                                              leave.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                                              leave.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                                              'bg-red-50 text-red-700'
                                            }`}>
                                              {leave.status}
                                            </Badge>
                                          </p>
                                          {leave.reason && (
                                            <p className="text-xs text-neutral-600 mt-1 border-t pt-1">{leave.reason}</p>
                                          )}
                                        </div>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                              {dateLeaves.length > 3 && (
                                <div className="text-[10px] md:text-xs text-neutral-600 font-medium text-center">
                                  +{dateLeaves.length - 3}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-6">
          <div className="glass-card p-3 md:p-4 shadow-soft">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs md:text-sm text-neutral-600">Total</p>
                <p className="text-lg md:text-xl font-bold text-charcoal-900">{leaves.length}</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-3 md:p-4 shadow-soft">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs md:text-sm text-neutral-600">Approved</p>
                <p className="text-lg md:text-xl font-bold text-emerald-600">{leaves.filter(l => l.status === 'approved').length}</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-3 md:p-4 shadow-soft">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs md:text-sm text-neutral-600">Pending</p>
                <p className="text-lg md:text-xl font-bold text-amber-600">{leaves.filter(l => l.status === 'pending').length}</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-3 md:p-4 shadow-soft">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <XCircle className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs md:text-sm text-neutral-600">Rejected</p>
                <p className="text-lg md:text-xl font-bold text-red-600">{leaves.filter(l => l.status === 'rejected').length}</p>
              </div>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </TooltipProvider>
  );
}
